import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Grid, Paper, TextField, Typography, InputAdornment, List, ListItem, ListItemText, Divider, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Stack, Avatar, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch, CircularProgress, FormHelperText, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert } from '@mui/material';
import { Search as SearchIcon, Delete as DeleteIcon, Add as AddIcon, Remove as RemoveIcon, Close as CloseIcon } from '@mui/icons-material';
import kioskApi, { customers, products } from '../services/kioskApi';
import { get, post } from '../utils/api';
import { useNavigate, useLocation } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import AddDevoteeDialog from '../components/AddDevoteeDialog';
import { isInternalNoEmail, sanitizeEmail } from '../utils/customerEmail';
import { ICCT_ORDER_META } from '../constants/icctWooOrderMeta';

/** MUI Select menus use Modal under the hood; swapping the whole page to the success view can strand scroll-lock/backdrop until refresh. */
const POST_PAYMENT_SELECT_MENU_PROPS = { disableScrollLock: true };

/** Today's date (YYYY-MM-DD) in ICCT's business timezone (Central), NOT the browser/UTC day — avoids the late-night off-by-one. */
function chicagoTodayIso() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(new Date());
}

const POST_PAYMENT_STATUS_LABEL_ID = 'post-payment-status-label';
const POST_PAYMENT_POSTING_TYPE_LABEL_ID = 'post-payment-posting-type-label';
const POST_PAYMENT_PLEDGE_LABEL_ID = 'post-payment-pledge-label';

/**
 * Statuses for Regular Payment and Pledge Payment: only pending and completed
 * (pledge-pending / pledge-completed are reserved for Pledge creation).
 */
const POST_PAYMENT_STANDARD_STATUSES = ['pending', 'completed'];

/** Ensures Select `value` always matches a MenuItem for the current posting type. */
function coercePostPaymentOrderStatus(paymentType, prev) {
  const v = String(prev ?? '').trim().toLowerCase();
  if (paymentType === 'pledge_creation') {
    if (v === 'pledge-pending' || v === 'pledge-completed') return v;
    return 'pledge-pending';
  }
  if (v === 'pledge-pending' || v === 'pledge-completed') return 'completed';
  if (POST_PAYMENT_STANDARD_STATUSES.includes(v)) return v;
  return 'completed';
}

/** Order total from local DB: same `orders.total_num` column as non-pledge orders (`total` is Woo REST only). */
function getPledgeOrderTotalAmount(pledgeOrder) {
  if (!pledgeOrder) return 0;
  if (pledgeOrder.total_num != null && pledgeOrder.total_num !== '') {
    const n = Number(pledgeOrder.total_num);
    if (Number.isFinite(n)) return n;
  }
  if (pledgeOrder.total != null && pledgeOrder.total !== '') {
    const n = Number(pledgeOrder.total);
    if (Number.isFinite(n)) return n;
  }
  const lines = Array.isArray(pledgeOrder.line_items) ? pledgeOrder.line_items : [];
  return lines.reduce((sum, it) => {
    const n = Number(it?.total_num ?? it?.total ?? it?.subtotal_num ?? 0);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
}

/** Short label for pledge order `status` in Post Payment pledge picker. */
function formatPledgeOrderStatusLabel(status) {
  const s = String(status || '').trim().toLowerCase();
  if (s === 'pledge-pending') return 'Pledge pending';
  if (s === 'pledge-completed') return 'Pledge completed';
  return s ? s.replace(/-/g, ' ') : '—';
}

/** Suffix from `balance_total` (GET /ldb/pledge-totals/by-customer); empty if unknown. */
function formatPledgeBalanceSuffix(order) {
  const balRaw = order?.balance_total;
  const balNum = balRaw != null && balRaw !== '' ? Number(balRaw) : NaN;
  if (!Number.isFinite(balNum)) return '';
  return ` · Balance $${balNum.toFixed(2)}`;
}

/** Pending pledges first, then completed; newest first within each group. */
function sortPledgesForDropdown(orders) {
  const rank = (s) => {
    const t = String(s || '').toLowerCase();
    if (t === 'pledge-pending') return 0;
    if (t === 'pledge-completed') return 1;
    return 2;
  };
  return [...(orders || [])].sort((a, b) => {
    const dr = rank(a.status) - rank(b.status);
    if (dr !== 0) return dr;
    const da = new Date(a.date_created_gmt || 0).getTime();
    const db = new Date(b.date_created_gmt || 0).getTime();
    return db - da;
  });
}

function pledgeDropdownDisplayText(order, lineLabel) {
  if (!order) return '';
  const total = Number(order.total_num || 0);
  const statusLabel = formatPledgeOrderStatusLabel(order.status);
  return `${lineLabel} • $${total.toFixed(2)} · ${statusLabel}${formatPledgeBalanceSuffix(order)}`;
}

const PostPaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Search terms
  const [userTerm, setUserTerm] = useState('');
  const [productTerm, setProductTerm] = useState('');

  // Results
  const [userResults, setUserResults] = useState([]);
  const [productResults, setProductResults] = useState([]);

  // Selection and cart
  const [selectedUser, setSelectedUser] = useState(null);
  const [cartItems, setCartItems] = useState([]); // {key, productId, variationId, name, price, qty, thumb, variationLabel, category}
  // Stable idempotency key for THIS order submission (survives a retry after an error; reset on success).
  // The check # is NOT used as the dedup key (numbers repeat across donors and collide).
  const idemKeyRef = useRef(null);

  // Variation dialog state
  const [varOpen, setVarOpen] = useState(false);
  const [varLoading, setVarLoading] = useState(false);
  const [varBaseProduct, setVarBaseProduct] = useState(null);
  const [variations, setVariations] = useState([]);
  const [varQtyById, setVarQtyById] = useState({}); // { [variationId]: qty }

  // Delete confirmation
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  // Adjustments
  const [feeAmount, setFeeAmount] = useState('0');
  const [discountAmount, setDiscountAmount] = useState('0');
  const [feeDescription, setFeeDescription] = useState('');
  const [discountDescription, setDiscountDescription] = useState('');

  // Payment & metadata
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [orderStatus, setOrderStatus] = useState('completed');
  const [txId, setTxId] = useState('');
  const [taxDeductible, setTaxDeductible] = useState(true);
  const [paymentDate, setPaymentDate] = useState(() => chicagoTodayIso());
  // Payment type: regular | pledge_payment | pledge_creation
  const [paymentType, setPaymentType] = useState('regular');
  const [pledges, setPledges] = useState([]);
  const [loadingPledges, setLoadingPledges] = useState(false);
  const [selectedPledgeId, setSelectedPledgeId] = useState('');
  const [pledgeSummary, setPledgeSummary] = useState('');
  // Payments dialog state
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentsError, setPaymentsError] = useState('');
  // Optional note for order (currently not rendered in UI)
  const [customerNote, setCustomerNote] = useState('');

  // Submit state
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [statusError, setStatusError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  /** Non-null → show full-page success (cart hidden; no modal). Prevents duplicate submits from Confirm & Save. */
  const [orderSuccess, setOrderSuccess] = useState(null); // { id: number }
  const [addDevoteeOpen, setAddDevoteeOpen] = useState(false);
  // The order is created LDB-first with a friendly order number (YYYY-NNNN) minted immediately — no
  // WooCommerce round-trip, no polling. One fetch reads it from the local order (falls back to the WC
  // number for historical orders, then to the local id).
  const [orderNo, setOrderNo] = useState(null);
  useEffect(() => {
    setOrderNo(null);
    if (!orderSuccess?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await get(`/ldb/orders/${orderSuccess.id}`);
        const n = r?.order?.order_number || r?.order?.woo_order_id;
        if (n && !cancelled) setOrderNo(n);
      } catch (e) { /* fall back to the local id in the display */ }
    })();
    return () => { cancelled = true; };
  }, [orderSuccess]);

  // MUI Dialog/Select can leave body scroll-lock or aria-hidden on #root after we unmount the form for the success screen — clicks appear dead until refresh.
  useLayoutEffect(() => {
    if (!orderSuccess) return;
    const releaseOverlays = () => {
      const body = document.body;
      const html = document.documentElement;
      body.style.overflow = '';
      body.style.paddingRight = '';
      body.style.pointerEvents = '';
      html.style.overflow = '';
      html.style.pointerEvents = '';
      const root = document.getElementById('root');
      if (root?.getAttribute('aria-hidden') === 'true') {
        root.removeAttribute('aria-hidden');
      }
      if (root?.hasAttribute('inert')) {
        root.removeAttribute('inert');
      }
    };
    releaseOverlays();
    const t = window.setTimeout(releaseOverlays, 0);
    return () => window.clearTimeout(t);
  }, [orderSuccess]);

  // Debounce helpers
  const userTimer = useRef(null);
  const productTimer = useRef(null);

  // Backend searchers
  const searchUsers = useCallback(async (term) => {
    const t = (term || '').trim().toLowerCase();
    if (t.length < 2) { setUserResults([]); return; }
    try {
      const users = await customers.searchLocal(t, 1, 25);
      setUserResults(Array.isArray(users) ? users.slice(0, 25) : []);
    } catch (e) {
      console.error('PostPayment user search (local) failed:', e);
      setUserResults([]);
    }
  }, []);

  // Preselect devotee if provided via route state or query param customer_id
  useEffect(() => {
    (async () => {
      try {
        if (selectedUser) return; // already chosen

        const stateUser = location.state?.user;
        const stateCid = stateUser?.customer_id || stateUser?.id || stateUser?.user_id || stateUser?.ID;
        const params = new URLSearchParams(location.search || '');
        const urlCid = params.get('customer_id');
        const cid = urlCid || stateCid;

        if (!cid) {
          if (stateUser && stateCid) {
            setSelectedUser(stateUser);
          }
          return;
        }

        console.log('[PostPayment] Loading user by customer_id:', cid, 'url?', Boolean(urlCid));

        let localUser = null;
        try {
          localUser = await customers.getLocalById(cid);
        } catch {}

        let resolvedUser = localUser || null;

        // LDB-first: resolve the donor from the local customers mirror only. No realtime WooCommerce
        // read here (Woo is batch-only) — customers are written LDB-first, so the local row carries the
        // name; if it doesn't, we fall back to the order's own state below rather than calling WC.

        if (!resolvedUser && stateUser) {
          resolvedUser = stateUser;
        }

        if (resolvedUser && (resolvedUser.customer_id || resolvedUser.id || resolvedUser.user_id || resolvedUser.ID)) {
          setSelectedUser(resolvedUser);
        }
      } catch {}
    })();
  }, [location.state, location.search, selectedUser]);

  // Arriving from Stripe Reconciliation "Create order": prefill the Stripe payment id into Tx ID and
  // default the method to card, so the booked order carries tx_id=<PI> and the recon row auto-matches.
  const reconPrefill = location.state?.reconPrefill || null;
  useEffect(() => {
    if (!reconPrefill) return;
    if (reconPrefill.paymentIntentId) setTxId((prev) => prev || reconPrefill.paymentIntentId);
    setPaymentMethod((prev) => (prev === 'cod' ? 'stripe_cc' : prev));
  }, [reconPrefill]);

  // Stable derived customer ID for the selected user
  const selectedCustomerId = React.useMemo(() => {
    if (!selectedUser) return null;
    const id = selectedUser.id || selectedUser.customer_id || selectedUser.user_id || selectedUser.ID;
    return id ? parseInt(id, 10) : null;
  }, [selectedUser]);

  // Guard against overlapping pledge requests
  const pledgesReqId = useRef(0);
  const submitInFlight = useRef(false);

  // Load pledges for selected user when Payment Type is pledge_payment
  useEffect(() => {
    const loadPledges = async () => {
      if (paymentType !== 'pledge_payment') {
        console.log('[Pledges] Skipping load: paymentType', paymentType);
        setPledges([]); setSelectedPledgeId(''); setPledgeSummary('');
        return;
      }
      const custId = selectedCustomerId;
      if (!custId) {
        console.log('[Pledges] Skipping load: no selectedUser id. selectedUser=', selectedUser);
        setPledges([]); setSelectedPledgeId(''); setPledgeSummary('');
        return;
      }
      try {
        setLoadingPledges(true);
        const reqId = ++pledgesReqId.current;
        console.log('[Pledges] Loading (ldb) for user', custId, 'reqId', reqId);
        // 1) List pledge-pending and pledge-completed orders from local DB (API allows one status per call)
        const [listPending, listCompleted] = await Promise.all([
          get('/ldb/orders', { customer_id: custId, status: 'pledge-pending', per_page: 100 }),
          get('/ldb/orders', { customer_id: custId, status: 'pledge-completed', per_page: 100 }),
        ]);
        const pendingRows = Array.isArray(listPending?.data) ? listPending.data : [];
        const completedRows = Array.isArray(listCompleted?.data) ? listCompleted.data : [];
        const byId = new Map();
        for (const o of pendingRows) byId.set(o.id, o);
        for (const o of completedRows) {
          if (!byId.has(o.id)) byId.set(o.id, o);
        }
        const baseArr = sortPledgesForDropdown([...byId.values()]);
        // 2) Line items per order + centralized paid/balance (same rule as Devotee / Pledges page)
        const [detailed, totRes] = await Promise.all([
          Promise.all(baseArr.map(async (o) => {
            try {
              const det = await get(`/ldb/orders/${o.id}`);
              // Merge so existing UI expecting Woo-like shape works
              return { ...o, line_items: Array.isArray(det?.items) ? det.items.map(it => ({
                id: it.item_id || it.id,
                product_id: it.product_id,
                variation_id: it.variation_id,
                name: it.name,
                quantity: it.quantity,
                total: String(it.total_num ?? it.price_num ?? 0),
                meta_data: [],
                image: it.image_url ? { src: it.image_url } : undefined
              })) : [] };
            } catch { return { ...o, line_items: [] }; }
          })),
          get('/ldb/pledge-totals/by-customer', { customer_id: custId }).catch((err) => {
            console.warn('[Pledges] pledge-totals/by-customer failed', err?.message || err);
            return { data: [] };
          }),
        ]);
        const totalsById = new Map(
          (Array.isArray(totRes?.data) ? totRes.data : []).map((r) => [r.id, r])
        );
        const withBalances = detailed.map((o) => {
          const t = totalsById.get(o.id);
          if (!t) return { ...o };
          return {
            ...o,
            balance_total: Number(t.balance_total ?? 0),
            paid_total: Number(t.paid_total ?? 0),
          };
        });
        if (reqId === pledgesReqId.current) {
          setPledges(withBalances);
          console.log('[Pledges][ldb] Loaded', withBalances.length, 'items (reqId', reqId, ')');
        } else {
          console.log('[Pledges] Discarding stale response (reqId', reqId, ')');
        }
      } catch (e) {
        console.warn('Failed to load pledges for customer', e?.message || e);
        setPledges([]);
      } finally {
        setLoadingPledges(false);
      }
    };
    loadPledges();
  }, [paymentType, selectedCustomerId, selectedUser]);



  // Open payments dialog and load history for selected pledge product/variation
  const onViewPayments = useCallback(async () => {
    try {
      setPaymentsOpen(true);
      setPaymentsLoading(true);
      setPaymentsError('');
      setPayments([]);
      const custId = selectedCustomerId;
      if (!custId) {
        setPaymentsError('Select a Devotee first');
        return;
      }
      const ord = pledges.find(p => String(p.id) === String(selectedPledgeId));
      const li = Array.isArray(ord?.line_items) && ord.line_items.length ? ord.line_items[0] : null;
      if (!li) {
        setPaymentsError('No pledge line item found to match payments');
        return;
      }
      const pid = li.product_id || null;
      const vid = li.variation_id;
      let allOrders = [];
      let pageNum = 1;
      const perPageNum = 100;
      while (true) {
        const res = await get('/ldb/orders/search', {
          customer_id: custId,
          status: 'completed',
          page: pageNum,
          per_page: perPageNum
        });
        const batch = Array.isArray(res?.data) ? res.data : [];
        allOrders = allOrders.concat(batch);
        const total = Number(res?.meta?.total || 0);
        if (!batch.length || batch.length < perPageNum || pageNum >= Math.ceil(total / perPageNum)) break;
        pageNum += 1;
      }
      // Filter orders whose items include same product/variation
      const matches = allOrders.flatMap(o => {
        const lines = Array.isArray(o.items) ? o.items : (Array.isArray(o.line_items) ? o.line_items : []);
        const matched = lines.filter(x => {
          if (String(x.product_id || '') !== String(pid || '')) return false;
          if (vid === null || vid === undefined || vid === '') return true;
          return String(x.variation_id || 0) === String(vid);
        });
        if (!matched.length) return [];
        return matched.map(x => ({
          orderId: o.id,
          date: o.date_created_gmt || o.date_created,
          qty: Number(x.quantity || 0),
          lineTotal: Number(x.total_num ?? x.total ?? 0)
        }));
      });
      setPayments(matches);
    } catch (e) {
      setPaymentsError(e?.message || 'Failed to load payments');
    } finally {
      setPaymentsLoading(false);
    }
  }, [selectedCustomerId, pledges, selectedPledgeId]);

  // When switching to any other Posting Type, clear the cart
  useEffect(() => {
    if (paymentType !== 'pledge_payment') {
      setCartItems([]);
    }
  }, [paymentType]);

  // Align status to posting type whenever posting type changes (not on every status click — that broke pledge status).
  useLayoutEffect(() => {
    setOrderStatus((prev) => coercePostPaymentOrderStatus(paymentType, prev));
    setStatusError('');
  }, [paymentType]);

  // Payment method titles map to generate label for payload
  const PAYMENT_TITLES = useMemo(() => ({
    'stripe_cc': 'Credit Card (Stripe)',
    'stripe_applepay': 'Apple Pay (Stripe)',
    'stripe_googlepay': 'Google Pay (Stripe)',
    'ppcp-gateway': 'PayPal (PPCP)',
    'amazon_payments_advanced': 'Amazon Pay',
    'stripe_ach': 'ACH Bank Transfer (Stripe)',
    'cheque': 'Cheque',
    'cod': 'Cash',
    'alg_custom_gateway_1': 'Zelle'
  }), []);

  const getChicagoTodayIsoDate = useCallback(() => {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date());
  }, []);

  const isPaidStatus = useCallback((s) => {
    const v = String(s || '').toLowerCase();
    return v === 'completed' || v === 'pledge-completed';
  }, []);

  // Build billing from selected user
  const buildBillingFromUser = useCallback((user) => {
    if (!user) return {};
    const b = user.billing || {};
    return {
      first_name: b.first_name || user.first_name || '',
      last_name: b.last_name || user.last_name || '',
      company: b.company || '',
      address_1: b.address_1 || '',
      address_2: b.address_2 || '',
      city: b.city || '',
      state: b.state || '',
      postcode: b.postcode || '',
      country: b.country || 'US',
      email: b.email || user.email || '',
      phone: b.phone || user.phone || ''
    };
  }, []);

  const selectedBillingEmail = useMemo(() => sanitizeEmail(buildBillingFromUser(selectedUser).email || ''), [buildBillingFromUser, selectedUser]);
  const selectedHasInternalNoEmail = useMemo(() => isInternalNoEmail(selectedBillingEmail), [selectedBillingEmail]);

  // Build payload and submit order
  const onConfirmSave = useCallback(async () => {
    if (submitInFlight.current) return;
    submitInFlight.current = true;
    try {
      setSubmitLoading(true);
      setSubmitError('');
      setFieldErrors({});

      // Validate required fields
      const errors = {};

      if (!selectedUser) {
        errors.devotee = 'Please select a Devotee';
      }
      
      if (!cartItems || cartItems.length === 0) {
        errors.items = 'Please add at least one item to the cart';
      }
      
      if (!paymentDate) {
        errors.paymentDate = 'Payment Date is required';
      }
      
      if (!paymentMethod) {
        errors.paymentMethod = 'Payment Method is required';
      }
      
      if (!orderStatus) {
        errors.status = 'Status is required';
      }
      
      // Validate status based on posting type
      const os = String(orderStatus || '').trim().toLowerCase();
      const isPledgeOnly = os === 'pledge-pending' || os === 'pledge-completed';
      if (paymentType === 'pledge_creation') {
        if (!isPledgeOnly) {
          errors.status = 'Pledge creation must use Pledge Pending or Pledge Completed';
        }
      } else if (isPledgeOnly) {
        errors.status = 'This posting type cannot use Pledge Pending or Pledge Completed';
      } else if (!POST_PAYMENT_STANDARD_STATUSES.includes(os)) {
        errors.status = 'Invalid status for this posting type';
      }
      
      // Also check if there's a status error from the dropdown validation
      if (statusError) {
        errors.status = statusError;
      }
      
      // If there are validation errors, show them and stop - DO NOT CREATE ORDER
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setSubmitError('Please fix all validation errors below before creating the order.');
        setSubmitLoading(false);
        // Scroll to top to show the error message
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // Compute line items with totals
      const line_items = cartItems.map(it => ({
        product_id: it.productId,
        ...(it.variationId ? { variation_id: it.variationId } : {}),
        // Carry the human-readable name the cart already has so the LOCAL order stores it immediately
        // (order_items.name). WC is just a ledger and may not have synced yet; the WC-create payload
        // builder ignores `name` (wooOrderPayload.js cherry-picks id/product/variation/qty/total/subtotal),
        // so this is local-only and does not affect the Woo sync.
        ...(it.name ? { name: it.name } : {}),
        quantity: it.qty || 1,
        total: (Number(it.qty || 1) * Number(it.price || 0)).toFixed(2)
      }));

      // Fee lines from UI - handle fees and discounts like OrderEditDialog
      const fee_lines = [];
      const feeNum = Math.max(0, Number(feeAmount) || 0);
      if (feeNum > 0) {
        fee_lines.push({ name: feeDescription || 'Credit Card Processing Fee', total: feeNum.toFixed(2) });
      }

      const discountNum = Math.max(0, Number(discountAmount) || 0);
      // Add discount as negative fee in fee_lines (same pattern as OrderEditDialog)
      if (discountNum > 0) {
        fee_lines.push({ name: discountDescription || 'Discount', total: (-discountNum).toFixed(2) });
      }

      // Meta: portal source + Woo snippet icct_send_order_emails (see src/constants/icctWooOrderMeta.js)
      const meta_data = [
        { key: ICCT_ORDER_META.ORDER_SOURCE_PRIVATE, value: ICCT_ORDER_META.PAYMENT_PORTAL },
        { key: ICCT_ORDER_META.ORDER_SOURCE, value: ICCT_ORDER_META.PAYMENT_PORTAL },
      ];
      // Only set pledge-related meta fields for pledge_payment when a pledge is selected.
      // wc_pledge_id / pledge_id identify the pledge ORDER — do not send pledge_product_id:
      // the live Woo pledge hook misreads it as an order id, loads WC_Product_Simple, then
      // calls get_items() and throws after the payment order is already saved in WooCommerce.
      if (paymentType === 'pledge_payment' && selectedPledgeId) {
        meta_data.push({ key: 'wc_pledge_id', value: String(selectedPledgeId) });
        meta_data.push({ key: 'pledge_id', value: String(selectedPledgeId) });
        if (cartItems.length > 0) {
          const firstItem = cartItems[0];
          if (firstItem.variationId) {
            meta_data.push({ key: 'pledge_variation_id', value: String(firstItem.variationId) });
          }
        }
      }
      meta_data.push({ key: 'is_tax_deductible', value: taxDeductible ? 'yes' : 'no' });
      if (discountNum > 0 && discountDescription) {
        meta_data.push({ key: 'discount_label', value: String(discountDescription) });
      }
      // Woo snippet suppresses automatic transactional mail; receipts are manual (resend-receipt) only.
      meta_data.push({ key: ICCT_ORDER_META.SEND_ORDER_EMAILS, value: ICCT_ORDER_META.NO });

      const txTrim = String(txId || '').trim();

      // Build payload - WooCommerce doesn't allow creating orders with non-current dates
      // We'll create with current date, then update via custom endpoint if needed
      const paymentDateOnly = String(paymentDate || '').slice(0, 10);
      const todayChicago = getChicagoTodayIsoDate();
      const needsDateUpdate = Boolean(paymentDateOnly && paymentDateOnly !== todayChicago);
      const finalPaidStatus = isPaidStatus(orderStatus);
      
      // One stable dedup key per submission — reused on a retry after an error, so a network retry can't
      // create a second order; reset on success. Separate from the check # (tx_id), which is reference data.
      if (!idemKeyRef.current) {
        idemKeyRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID() : `postpay-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }

      const commitPayload = {
        customer_id: selectedCustomerId,
        line_items,
        fee_lines,
        billing: buildBillingFromUser(selectedUser),
        meta_data,
        payment_method: paymentMethod,
        payment_method_title: PAYMENT_TITLES[paymentMethod] || 'Payment',
        final_status: orderStatus,
        set_paid: needsDateUpdate ? false : finalPaidStatus,
        payment_date: paymentDateOnly || undefined,
        customer_note: customerNote || undefined,
        idempotency_key: idemKeyRef.current,
      };

      if (paymentType === 'pledge_payment' && selectedPledgeId) {
        commitPayload.pledge_order_id = selectedPledgeId;
      }

      if (txTrim) {
        commitPayload.tx_id = txTrim;
      }

      console.log('[Order] Submitting payload (wp commit)', commitPayload);
      let createdId = null;
      try {
        const res = await post('/api/orders/commit', commitPayload, { timeout: 120000 });
        createdId = res?.id;
        console.log('[Order] Created (wp commit)', res);
      } catch (error) {
        // Check if error response contains an order ID (partial success)
        console.error('[Order] Creation error', error);
        if (error?.data?.id) {
          console.log('[Order] Order ID found in error response:', error.data.id);
          createdId = error.data.id;
          console.log('[Order] Continuing with order ID despite error:', error.message);
        } else {
          throw error;
        }
      }
      
      if (!createdId) {
        setSubmitError('Order was created but no order ID was returned. Please check the orders list.');
        setSubmitLoading(false);
        return;
      }
      
      // For pledge payment, ensure order is synced locally (the backend should handle this via sync process)
      // The pledge_id meta field is already set in the order, and backend will sync it
      if (paymentType === 'pledge_payment' && createdId) {
        console.log('[Order] Pledge payment order created with pledge_id in meta_data');
      }
      
      if (createdId) {
        // Close portaled MUI layers before swapping to success UI so ModalManager / Select menus tear down cleanly.
        if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        setPaymentsOpen(false);
        setVarOpen(false);
        setDeleteOpen(false);
        setAddDevoteeOpen(false);
        idemKeyRef.current = null; // order booked — next submission gets a fresh dedup key
        setOrderSuccess({ id: createdId });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setSubmitError('Order was created but no order ID was returned. Please check the orders list.');
      }
    } catch (e) {
      console.error('Order creation failed', e);
      const rawMsg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Failed to create order';
      const maybeCreatedInWoo = /get_items\(\)/i.test(String(rawMsg));
      setSubmitError(
        maybeCreatedInWoo
          ? `${rawMsg} The payment may already exist in WooCommerce — check Orders before submitting again.`
          : rawMsg
      );
    } finally {
      submitInFlight.current = false;
      setSubmitLoading(false);
    }
  }, [selectedUser, cartItems, feeAmount, discountAmount, feeDescription, discountDescription, paymentType, selectedPledgeId, orderStatus, paymentMethod, PAYMENT_TITLES, selectedCustomerId, paymentDate, taxDeductible, customerNote, statusError, getChicagoTodayIsoDate, isPaidStatus]);

  const clearSuccessAndCart = useCallback(() => {
    setOrderSuccess(null);
    setCartItems([]);
    setFeeAmount('0');
    setDiscountAmount('0');
    setFeeDescription('');
    setDiscountDescription('');
    setCustomerNote('');
    setTxId('');
    setPaymentDate(chicagoTodayIso());
    setOrderStatus(coercePostPaymentOrderStatus(paymentType, 'completed'));
    setPaymentMethod('cod');
    setStatusError('');
    setFieldErrors({});
    setSubmitError('');
  }, [paymentType]);

  const handleSuccessAddAnotherSameCustomer = useCallback(() => {
    clearSuccessAndCart();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [clearSuccessAndCart]);

  const handleSuccessAddNewOrder = useCallback(() => {
    clearSuccessAndCart();
    setSelectedUser(null);
    setUserResults([]);
    setPaymentType('regular');
    setUserTerm('');
    const params = new URLSearchParams(location.search || '');
    if (params.has('customer_id') || location.state?.user) {
      navigate('/post-payment', { replace: true, state: null });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [clearSuccessAndCart, navigate, location.search, location.state]);

  const buildItemLabel = useCallback((order) => {
    if (!order) return '';
    const li = Array.isArray(order.line_items) && order.line_items.length ? order.line_items[0] : null;
    if (!li) return `Order #${order.id}`;
    let attr = '';
    const meta = Array.isArray(li.meta_data) ? li.meta_data : [];
    const attrPairs = meta
      .filter(m => typeof m.key === 'string' && (m.key.startsWith('pa_') || m.key.startsWith('attribute_')) && m.value)
      .map(m => `${m.key.replace(/^attribute_|^pa_/, '')}: ${m.value}`);
    if (attrPairs.length) attr = ` - ${attrPairs.join(', ')}`;
    return `${li.name}${attr}`;
  }, []);

  const searchProducts = useCallback(async (term) => {
    const t = (term || '').trim();
    if (!t) { setProductResults([]); return; }
    try {
      const { data } = await products.searchLocal(t, 1, 25);
      // Map local rows to expected shape for UI list
      const mapped = (Array.isArray(data) ? data : []).map(p => ({
        id: p.id,
        name: p.name,
        sku: p.sku,
        type: p.type,
        price: p.price_num,
        regular_price: p.price_num,
        images: p.image_url ? [{ src: p.image_url }] : [],
      }));
      setProductResults(mapped);
    } catch (e) {
      console.error('PostPayment product search (local) failed:', e);
      setProductResults([]);
    }
  }, []);

  // Debounced searching
  useEffect(() => {
    if (userTimer.current) clearTimeout(userTimer.current);
    userTimer.current = setTimeout(() => { void searchUsers(userTerm); }, 400);
    return () => clearTimeout(userTimer.current);
  }, [userTerm, searchUsers]);

  useEffect(() => {
    if (productTimer.current) clearTimeout(productTimer.current);
    productTimer.current = setTimeout(() => { void searchProducts(productTerm); }, 300);
    return () => clearTimeout(productTimer.current);
  }, [productTerm, searchProducts]);

  // Handlers
  const onSelectUser = useCallback((u) => {
    console.log('[Devotee] Selected user raw:', u);
    setSelectedUser(u);
    // Clear user search box and results
    setUserTerm('');
    setUserResults([]);
    // Clear devotee error if user is selected
    if (fieldErrors.devotee) {
      setFieldErrors(prev => ({ ...prev, devotee: '' }));
    }
  }, [fieldErrors.devotee]);

  const onAddDevoteePostPayment = useCallback((devotee) => {
    setAddDevoteeOpen(false);
    const cid = devotee?.customer_id || devotee?.id || devotee?.user_id || devotee?.ID;
    if (cid) {
      navigate(`/post-payment?customer_id=${encodeURIComponent(cid)}`, { state: { user: devotee } });
      return;
    }
    navigate('/post-payment', { state: { user: devotee } });
  }, [navigate]);

  const addItemToCart = useCallback((item) => {
    setCartItems((prev) => {
      // If same product+variation exists, increase qty
      const idx = prev.findIndex(ci => ci.productId === item.productId && (ci.variationId || null) === (item.variationId || null));
      const newItems = idx >= 0 
        ? (() => {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + (item.qty || 1) };
        return next;
          })()
        : [...prev, { ...item, key: `${item.productId}-${item.variationId || 'base'}-${Date.now()}` }];
      
      // Clear items error if items are added
      if (newItems.length > 0 && fieldErrors.items) {
        setFieldErrors(prev => ({ ...prev, items: '' }));
      }
      
      return newItems;
    });
  }, [fieldErrors.items]);

  const onSelectProduct = useCallback(async (p) => {
    // For pledge-related orders, allow only one product/variation
    const isPledgeRelated = paymentType === 'pledge_payment' || paymentType === 'pledge_creation';
    if (isPledgeRelated && cartItems.length > 0) {
      alert('For pledge-related orders, only one product/variation is allowed in the cart.');
      return;
    }

    // If product is variable, fetch variations from local DB if not present
    if (p?.type === 'variable') {
      try {
        setVarBaseProduct(p);
        setVarOpen(true);
        setVarLoading(true);
        setVarQtyById({});
        let vars = Array.isArray(p?.variations) ? p.variations : [];
        if (!vars.length) {
          const detail = await products.getLocalById(p.id);
          const vs = Array.isArray(detail?.variations) ? detail.variations : [];
          // Map local variation rows to UI shape
          vars = vs.map(v => ({
            id: v.id,
            price: v.price_num ?? v.regular_price_num ?? 0,
            regular_price: v.regular_price_num ?? v.price_num ?? 0,
            attributes: Array.isArray(v.attributes) ? v.attributes : [],
            image: v.image_url ? { src: v.image_url } : undefined,
            name: ''
          }));
        }
        setVariations(vars);
        const initialQty = {};
        vars.forEach(v => { if (v && v.id) initialQty[v.id] = 1; });
        setVarQtyById(initialQty);
        setVarLoading(false);
        return;
      } catch (e) {
        console.warn('Failed to load local variations for product', p?.id, e?.message || e);
        setVarOpen(false);
      }
    }
    // Simple product
    const price = Number(p?.price || p?.regular_price || 0);
    const thumb = (Array.isArray(p?.images) && p.images[0]?.src) || '';
    const category = (Array.isArray(p?.categories) && p.categories[0]?.name) || '';
    addItemToCart({ productId: p.id, name: p.name, price, qty: 1, thumb, variationLabel: '', category });
    // Clear product search box and results
    setProductTerm('');
    setProductResults([]);
  }, [addItemToCart, paymentType, cartItems.length]);

  const onSelectVariation = useCallback((v, qtyInput) => {
    if (!varBaseProduct) return;
    
    // For pledge-related orders, allow only one product/variation
    const isPledgeRelated = paymentType === 'pledge_payment' || paymentType === 'pledge_creation';
    if (isPledgeRelated && cartItems.length > 0) {
      alert('For pledge-related orders, only one product/variation is allowed in the cart.');
      setVarOpen(false);
      setVarBaseProduct(null);
      setVariations([]);
      return;
    }

    const attrLabel = (v?.attributes && v.attributes.length)
      ? v.attributes.map(a => `${a.name}: ${a.option}`).join(', ')
      : (v?.name || '');
    const name = `${varBaseProduct.name}${attrLabel ? ` - ${attrLabel}` : ''}`;
    const price = Number(v?.price || v?.regular_price || varBaseProduct?.price || 0);
    const qty = Math.max(1, parseInt(qtyInput) || 1);
    const thumb = (v?.image?.src) || (Array.isArray(varBaseProduct?.images) && varBaseProduct.images[0]?.src) || '';
    const category = (Array.isArray(varBaseProduct?.categories) && varBaseProduct.categories[0]?.name) || '';
    addItemToCart({ productId: varBaseProduct.id, variationId: v.id, name, price, qty, thumb, variationLabel: attrLabel, category });
    setVarOpen(false);
    setVarBaseProduct(null);
    setVariations([]);
    // Clear product search box and results
    setProductTerm('');
    setProductResults([]);
  }, [addItemToCart, varBaseProduct, paymentType, cartItems.length]);

  const updateItem = useCallback((key, updates) => {
    setCartItems(prev => prev.map(ci => ci.key === key ? { ...ci, ...updates } : ci));
  }, []);

  const removeItem = useCallback((key) => {
    setCartItems(prev => prev.filter(ci => ci.key !== key));
  }, []);

  const renderUsers = useMemo(() => (
    <List dense disablePadding>
      {userResults.map(u => {
        const primary = [u.first_name, u.last_name].filter(Boolean).join(' ') || 'No Name';
        const email = u.email || u.billing?.email || '';
        const phone = u.phone || u.billing?.phone || '';
        return (
          <React.Fragment key={u.id || `${primary}-${email}`}>
            <ListItem button onClick={() => onSelectUser(u)}
              secondaryAction={
                <Button variant="outlined" size="small" onClick={(e) => { e.stopPropagation(); onSelectUser(u); }}>
                  Select
                </Button>
              }>
              <ListItemText primary={primary} secondary={[email, phone].filter(Boolean).join(' • ')} />
            </ListItem>
            <Divider />
          </React.Fragment>
        );
      })}
      {userResults.length === 0 && userTerm.trim().length >= 5 && (
        <ListItem>
          <ListItemText primary="No matching devotees" />
        </ListItem>
      )}
    </List>
  ), [userResults, userTerm]);

  const renderProducts = useMemo(() => (
    <List dense disablePadding>
      {productResults.map(p => {
        const cat = Array.isArray(p.categories) && p.categories[0]?.name ? ` • ${p.categories[0].name}` : '';
        return (
          <React.Fragment key={p.id}>
            <ListItem button onClick={() => onSelectProduct(p)}>
              <ListItemText primary={p.name} secondary={[p.sku, cat].filter(Boolean).join(' ')} />
            </ListItem>
            <Divider />
          </React.Fragment>
        );
      })}
      {productResults.length === 0 && productTerm.trim().length > 0 && (
        <ListItem>
          <ListItemText primary="No matching products" />
        </ListItem>
      )}
    </List>
  ), [productResults, productTerm]);

  if (orderSuccess) {
    const sid = orderSuccess.id;
    const donorProfileId =
      selectedUser?.customer_id ||
      selectedUser?.id ||
      selectedUser?.user_id ||
      selectedUser?.ID ||
      null;
    const successBtnProps = {
      variant: 'contained',
      color: 'primary',
      size: 'large',
      sx: {
        py: 1.5,
        px: 3,
        fontSize: '1rem',
        minHeight: 52,
        textTransform: 'none',
      },
    };
    return (
      <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
        <Typography variant="h5" gutterBottom>Order created successfully</Typography>
        <Paper sx={{ p: 3, maxWidth: 880, width: '100%', mx: 'auto', mt: 1 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="bold">Order #{orderNo || sid}</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Your order has been created. A receipt is on its way to the email on file.
            </Typography>
          </Alert>
          <Alert
            severity="info"
            sx={{ mb: 2, alignItems: 'center' }}
            action={
              <Button
                {...successBtnProps}
                onClick={async () => {
                  try {
                    const billingEmail = sanitizeEmail(buildBillingFromUser(selectedUser).email || '');
                    if (isInternalNoEmail(billingEmail)) {
                      throw new Error("Cannot send receipt: this email is not customer's real email and was internally assigned.");
                    }
                    await post(`/api/orders/${sid}/resend-receipt`, billingEmail ? { email: billingEmail } : {});
                    alert('Receipt sent successfully');
                  } catch (err) {
                    alert(`Failed to send receipt: ${err?.response?.data?.message || err?.message || 'Unknown error'}`);
                  }
                }}
                disabled={selectedHasInternalNoEmail}
              >
                Send Email Receipt
              </Button>
            }
          >
            Receipt is not sent automatically. Use the button to email it when needed.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use the buttons below to add another order or go to the orders list.
          </Typography>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}>
              <Button {...successBtnProps} onClick={handleSuccessAddAnotherSameCustomer}>
                Add another order (same customer)
              </Button>
              <Button
                {...successBtnProps}
                disabled={!donorProfileId}
                onClick={() => {
                  if (!donorProfileId) return;
                  navigate(`/devotees/${encodeURIComponent(String(donorProfileId))}`);
                }}
              >
                Donor Profile
              </Button>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}>
              <Button {...successBtnProps} onClick={() => navigate('/orders')}>
                View orders
              </Button>
              <Button {...successBtnProps} onClick={handleSuccessAddNewOrder}>
                Add new order
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </PageLayout>
    );
  }

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Typography variant="h5" gutterBottom>Post Payment</Typography>

      {reconPrefill && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Recording Stripe payment <b>{reconPrefill.paymentIntentId}</b>
          {reconPrefill.amount ? <> for <b>${Number(reconPrefill.amount).toFixed(2)}</b></> : null}
          {reconPrefill.email ? <> · donor <b>{reconPrefill.name || reconPrefill.email}</b></> : null}.
          {' '}Confirm the donor and add the line items to match the amount, then submit. The Stripe id is prefilled in <b>Tx ID</b> so the payment links automatically.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Devotee</Typography>
        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
          <TextField 
            fullWidth 
            placeholder="Search by name, email or phone (from cache)" 
            value={userTerm}
            onChange={(e) => setUserTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="outlined"
            onClick={() => setAddDevoteeOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add Devotee
          </Button>
        </Box>
        <Box sx={{ mt: 1, maxHeight: 280, overflowY: 'auto' }}>
          {renderUsers}
        </Box>
        {fieldErrors.devotee && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{fieldErrors.devotee}</Typography>
        )}
        {selectedUser && (
          <Box sx={{ mt: 1, p: 1.5, border: '2px solid', borderColor: fieldErrors.devotee ? 'error.main' : 'primary.main', bgcolor: fieldErrors.devotee ? 'rgba(211,47,47,0.06)' : 'rgba(25,118,210,0.06)', borderRadius: 1, position: 'relative' }}>
            <Typography variant="subtitle2" color={fieldErrors.devotee ? 'error.main' : 'primary.main'}>Selected Devotee</Typography>
            <Typography variant="body2">{[
              selectedUser.first_name || selectedUser.billing?.first_name,
              selectedUser.last_name || selectedUser.billing?.last_name
            ].filter(Boolean).join(' ') || 'No Name'}</Typography>
            <Typography variant="caption" color="text.secondary">{selectedUser.email || selectedUser.billing?.email} {selectedUser.phone || selectedUser.billing?.phone ? `• ${selectedUser.phone || selectedUser.billing?.phone}` : ''}</Typography>
            <IconButton
              aria-label="Clear selected devotee"
              size="small"
              onClick={() => {
                setSelectedUser(null);
                setPledges([]);
                setSelectedPledgeId('');
                setPledgeSummary('');
                setUserTerm('');
                setUserResults([]);

                const params = new URLSearchParams(location.search || '');
                if (params.has('customer_id') || location.state?.user) {
                  navigate('/post-payment', { replace: true, state: null });
                }

                // Reset posting type to regular to avoid pledge-only UI without a devotee
                if (paymentType === 'pledge_payment') {
                  setPaymentType('regular');
                }
              }}
              sx={{ position: 'absolute', top: 6, right: 6 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Paper>

      {/* Payments History Dialog */}
      <Dialog open={paymentsOpen} onClose={() => setPaymentsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Past Payments</DialogTitle>
        <DialogContent dividers>
          {paymentsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading…</Typography>
            </Box>
          )}
          {!paymentsLoading && paymentsError && (
            <Typography variant="body2" color="error">{paymentsError}</Typography>
          )}
          {!paymentsLoading && !paymentsError && payments.length === 0 && (
            <Typography variant="body2" color="text.secondary">No payments found for this product/variation.</Typography>
          )}
          {!paymentsLoading && !paymentsError && payments.length > 0 && (
            <>
              {(() => {
                const pledgeOrder = pledges.find(p => String(p.id) === String(selectedPledgeId));
                const pledgeLabel = buildItemLabel(pledgeOrder);
                const pledgeTotal = getPledgeOrderTotalAmount(pledgeOrder);
                const paymentsTotal = payments.reduce((sum, x) => sum + (Number(x.lineTotal) || 0), 0);
                const remaining = pledgeTotal - paymentsTotal;
                const li = Array.isArray(pledgeOrder?.line_items) && pledgeOrder.line_items.length ? pledgeOrder.line_items[0] : null;
                const pid = li?.product_id ?? '';
                const vid = li?.variation_id ?? '';
                return (
                  <Box sx={{ mb: 1.5, p: 1.5, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
                    <Typography variant="subtitle2">Summary</Typography>
                    <Typography variant="body2" color="text.secondary">{pledgeLabel ? `${pledgeLabel}` : 'Selected pledge item'}</Typography>
                    <Typography variant="caption" color="text.secondary">PID: {pid}{vid ? ` • VID: ${vid}` : ''}</Typography>
                    <Stack direction="row" spacing={3} sx={{ mt: 0.5 }}>
                      <Typography variant="body2">Pledge Total: ${pledgeTotal.toFixed(2)}</Typography>
                      <Typography variant="body2">Payments Total: ${paymentsTotal.toFixed(2)}</Typography>
                      <Typography variant="body2" color={remaining > 0 ? 'warning.main' : (remaining < 0 ? 'error.main' : 'success.main')}>
                        Balance: ${remaining.toFixed(2)}
                      </Typography>
                    </Stack>
                  </Box>
                );
              })()}

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Order #</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((p, idx) => (
                      <TableRow key={`${p.orderId}-${idx}`}>
                        <TableCell>{new Date(p.date).toLocaleString()}</TableCell>
                        <TableCell>#{p.orderId}</TableCell>
                        <TableCell align="right">{p.qty}</TableCell>
                        <TableCell align="right">${p.lineTotal.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Payment Details */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Payment Details</Typography>
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          spacing={2}
          sx={{ mt: 1, columnGap: 2, rowGap: 1.5 }}
        >
          <FormControl size="small" sx={{ minWidth: 200, maxWidth: '100%' }}>
            <InputLabel id={POST_PAYMENT_POSTING_TYPE_LABEL_ID}>Posting type</InputLabel>
            <Select
              labelId={POST_PAYMENT_POSTING_TYPE_LABEL_ID}
              label="Posting type"
              value={paymentType}
              MenuProps={POST_PAYMENT_SELECT_MENU_PROPS}
              onChange={(e) => {
                const v = e.target.value;
                setPaymentType(v);
                setOrderStatus((prev) => coercePostPaymentOrderStatus(v, prev));
                setStatusError('');
              }}
            >
              <MenuItem value="regular">Regular Payment</MenuItem>
              <MenuItem value="pledge_payment">Pledge Payment</MenuItem>
              <MenuItem value="pledge_creation">Pledge Creation</MenuItem>
            </Select>
          </FormControl>
          {paymentType === 'pledge_payment' && (
            <>
              <FormControl size="small" sx={{ minWidth: 240, width: 'auto', maxWidth: '100%', display: 'inline-flex' }} error={!selectedCustomerId}>
                <InputLabel id={POST_PAYMENT_PLEDGE_LABEL_ID} shrink>
                  Select pledge
                </InputLabel>
                <Select
                  labelId={POST_PAYMENT_PLEDGE_LABEL_ID}
                  label="Select pledge"
                  value={!selectedCustomerId ? '' : loadingPledges ? '' : selectedPledgeId}
                  displayEmpty
                  autoWidth
                  MenuProps={POST_PAYMENT_SELECT_MENU_PROPS}
                  sx={{ '& .MuiSelect-select': { whiteSpace: 'nowrap' } }}
                  renderValue={(val) => {
                    if (!selectedCustomerId) {
                      return (
                        <Typography variant="body2" component="span" color="text.secondary">
                          Select a Devotee first
                        </Typography>
                      );
                    }
                    if (loadingPledges) {
                      return (
                        <Typography variant="body2" component="span" color="text.secondary">
                          Loading pledges…
                        </Typography>
                      );
                    }
                    if (!pledges.length) {
                      return (
                        <Typography variant="body2" component="span" color="text.secondary">
                          No pledges
                        </Typography>
                      );
                    }
                    if (!val) {
                      return (
                        <Typography variant="body2" component="span" color="text.secondary">
                          Choose a pledge
                        </Typography>
                      );
                    }
                    const ord = pledges.find((p) => String(p.id) === String(val));
                    if (!ord) return '';
                    const lineLabel = buildItemLabel(ord);
                    return pledgeDropdownDisplayText(ord, lineLabel);
                  }}
                  onChange={(e) => {
                    const rawVal = e.target.value;
                    const val = rawVal === '' ? '' : rawVal;
                    setSelectedPledgeId(val);
                    const ord = pledges.find((p) => String(p.id) === String(val));
                    if (!ord) {
                      setPledgeSummary('');
                      return;
                    }
                    const label = buildItemLabel(ord);
                    const total = Number(ord?.total_num || 0);
                    const statusPart = formatPledgeOrderStatusLabel(ord?.status);
                    setPledgeSummary(
                      `${label} • Order total: $${total.toFixed(2)} · ${statusPart}${formatPledgeBalanceSuffix(ord)}`
                    );
                    try {
                      const li = Array.isArray(ord?.line_items) && ord.line_items.length ? ord.line_items[0] : null;
                      if (li) {
                        const qty = Math.max(1, parseInt(li.quantity || '1', 10));
                        const lineTotal = Number(li.total || 0);
                        const unitPrice = qty > 0 ? lineTotal / qty : lineTotal;
                        const newItem = {
                          key: `pledge-${ord.id}-${li.product_id || 'p'}-${li.variation_id || 'v'}-${Date.now()}`,
                          productId: li.product_id || undefined,
                          variationId: li.variation_id || undefined,
                          name: buildItemLabel(ord) || (li.name || `Pledge #${ord.id}`),
                          price: unitPrice,
                          qty,
                          thumb: (li.image && (li.image.src || li.image.url)) || '',
                          variationLabel: '',
                          category: 'Pledge',
                        };
                        setCartItems((prev) => {
                          const exists = prev.some(
                            (ci) =>
                              ci.productId === newItem.productId &&
                              (ci.variationId || null) === (newItem.variationId || null) &&
                              ci.category === 'Pledge'
                          );
                          return exists ? prev : [...prev, newItem];
                        });
                      }
                    } catch { /* ignore */ }
                  }}
                  disabled={loadingPledges || !selectedCustomerId || !pledges.length}
                >
                  {/* MUI Select only registers direct MenuItem children — do not wrap in <> fragments */}
                  {!selectedCustomerId && (
                    <MenuItem value="" sx={{ display: 'none' }}>
                      &nbsp;
                    </MenuItem>
                  )}
                  {selectedCustomerId && loadingPledges && (
                    <MenuItem value="" sx={{ display: 'none' }}>
                      &nbsp;
                    </MenuItem>
                  )}
                  {selectedCustomerId && !loadingPledges && pledges.length === 0 && (
                    <MenuItem value="" disabled>
                      No pledges
                    </MenuItem>
                  )}
                  {selectedCustomerId && !loadingPledges && pledges.length > 0 && (
                    <MenuItem value="" disabled sx={{ opacity: 0.7 }}>
                      <em>Choose a pledge…</em>
                    </MenuItem>
                  )}
                  {selectedCustomerId &&
                    !loadingPledges &&
                    pledges.length > 0 &&
                    pledges.map((p) => {
                      const lineLabel = buildItemLabel(p);
                      return (
                        <MenuItem key={p.id} value={String(p.id)}>
                          {pledgeDropdownDisplayText(p, lineLabel)}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
              <Link
                component="button"
                variant="body2"
                onClick={onViewPayments}
                disabled={!selectedCustomerId || !selectedPledgeId}
                sx={{ opacity: !selectedCustomerId || !selectedPledgeId ? 0.5 : 1, whiteSpace: 'nowrap' }}
              >
                View payments
              </Link>
              {loadingPledges && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Loading pledges…
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Stack>
        {/* Row 1: Payment Date, Payment Method, Status */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={3}>
            <TextField
              label="Payment Date"
              type="date"
              value={paymentDate}
              onChange={(e) => {
                setPaymentDate(e.target.value);
                if (fieldErrors.paymentDate) {
                  setFieldErrors(prev => ({ ...prev, paymentDate: '' }));
                }
              }}
              fullWidth
              size="small"
              required
              error={!!fieldErrors.paymentDate}
              helperText={fieldErrors.paymentDate}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" sx={{ minWidth: 260 }} required error={!!fieldErrors.paymentMethod}>
              <InputLabel>Payment Method</InputLabel>
              <Select 
                label="Payment Method" 
                value={paymentMethod} 
                MenuProps={POST_PAYMENT_SELECT_MENU_PROPS}
                onChange={(e) => {
                  setPaymentMethod(e.target.value);
                  if (fieldErrors.paymentMethod) {
                    setFieldErrors(prev => ({ ...prev, paymentMethod: '' }));
                  }
                }}
              >
                <MenuItem value="stripe_cc">Credit Card (Stripe)</MenuItem>
                <MenuItem value="stripe_applepay">Apple Pay (Stripe)</MenuItem>
                <MenuItem value="stripe_googlepay">Google Pay (Stripe)</MenuItem>
                <MenuItem value="ppcp-gateway">PayPal (PPCP)</MenuItem>
                <MenuItem value="amazon_payments_advanced">Amazon Pay</MenuItem>
                <MenuItem value="stripe_ach">ACH Bank Transfer (Stripe)</MenuItem>
                <MenuItem value="cheque">Cheque</MenuItem>
                <MenuItem value="cod">Cash</MenuItem>
                <MenuItem value="alg_custom_gateway_1">Zelle</MenuItem>
              </Select>
              {fieldErrors.paymentMethod && <FormHelperText>{fieldErrors.paymentMethod}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" sx={{ minWidth: { xs: '100%', md: 260 } }} required error={!!statusError || !!fieldErrors.status}>
              <InputLabel id={POST_PAYMENT_STATUS_LABEL_ID} shrink>
                Status
              </InputLabel>
              {paymentType === 'pledge_creation' ? (
                <Select
                  native
                  labelId={POST_PAYMENT_STATUS_LABEL_ID}
                  id="post-payment-status-select-pledge"
                  label="Status"
                  value={orderStatus}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setOrderStatus(coercePostPaymentOrderStatus('pledge_creation', raw));
                    if (fieldErrors.status) {
                      setFieldErrors((prev) => ({ ...prev, status: '' }));
                    }
                    setStatusError('');
                  }}
                >
                  <option value="pledge-pending">Pledge pending</option>
                  <option value="pledge-completed">Pledge completed</option>
                </Select>
              ) : (
                <Select
                  key={`post-payment-status-${paymentType}`}
                  labelId={POST_PAYMENT_STATUS_LABEL_ID}
                  id="post-payment-status-select"
                  label="Status"
                  value={orderStatus}
                  MenuProps={POST_PAYMENT_SELECT_MENU_PROPS}
                  sx={{ '& .MuiSelect-select': { textTransform: 'capitalize' } }}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setOrderStatus(coercePostPaymentOrderStatus(paymentType, raw));
                    if (fieldErrors.status) {
                      setFieldErrors((prev) => ({ ...prev, status: '' }));
                    }
                    setStatusError('');
                  }}
                >
                  {POST_PAYMENT_STANDARD_STATUSES.map((s) => (
                    <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              )}
              {(statusError || fieldErrors.status) && <FormHelperText>{statusError || fieldErrors.status}</FormHelperText>}
            </FormControl>
          </Grid>
        </Grid>

        {/* Row 2: Tx ID / Check #, Tax Deductible */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={3}>
            <TextField label="Tx ID / Check #" value={txId} onChange={(e) => setTxId(e.target.value)} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel control={<Switch checked={taxDeductible} onChange={(e) => setTaxDeductible(e.target.checked)} />} label="Tax Deductible" />
          </Grid>
        </Grid>

        {/* Row 3: Notes - double width */}
        <Box sx={{ mt: 2, width: { xs: '100%', md: '66.67%' } }}>
            <TextField
              label="Notes"
              placeholder="Optional notes to include on the order (e.g., Annadanam on 2025-09-27)"
              value={customerNote}
              onChange={(e) => setCustomerNote(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            maxRows={2}
            />
        </Box>
      </Paper>

      {paymentType !== 'pledge_payment' && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6">Products</Typography>
          <TextField 
            fullWidth 
            placeholder="Search products" 
            sx={{ mt: 1 }}
            value={productTerm}
            onChange={(e) => setProductTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <Box sx={{ mt: 1, maxHeight: 280, overflowY: 'auto' }}>
            {renderProducts}
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Cart</Typography>
        {fieldErrors.items && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{fieldErrors.items}</Typography>
        )}
        {cartItems.length === 0 ? (
          <Typography variant="body2" color={fieldErrors.items ? 'error' : 'text.secondary'} sx={{ mt: 1 }}>No items yet. Select products above to add them here.</Typography>
        ) : (
          <List dense sx={{ mt: 1 }}>
            {cartItems.map(item => (
              <React.Fragment key={item.key}>
                <ListItem alignItems="center" sx={{ py: 1, bgcolor: 'rgba(25,118,210,0.04)', borderLeft: '4px solid', borderColor: 'primary.main' }}
                  secondaryAction={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <IconButton size="small" aria-label="delete" color="error" onClick={() => { setDeleteItem(item); setDeleteOpen(true); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  }>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                    <Avatar variant="rounded" src={item.thumb || undefined} alt={item.name} sx={{ width: 48, height: 48 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" color="primary.main">{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        PID: {item.productId}{item.variationId ? ` • VID: ${item.variationId}` : ''}{item.category ? ` • ${item.category}` : ''}
                      </Typography>
                    </Box>
                    <TextField
                      label="Price"
                      type="number"
                      size="small"
                      inputProps={{ min: 0, step: 0.01 }}
                      value={item.price}
                      onChange={(e) => updateItem(item.key, { price: Math.max(0, Number(e.target.value || 0)) })}
                      sx={{ width: 120 }}
                    />
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 120 }}>
                      <IconButton size="small" onClick={() => updateItem(item.key, { qty: Math.max(1, (item.qty || 1) - 1) })}>
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Box sx={{ px: 1, minWidth: 36, textAlign: 'center' }}>
                        <Typography variant="body2">{item.qty}</Typography>
                      </Box>
                      <IconButton size="small" onClick={() => updateItem(item.key, { qty: (item.qty || 1) + 1 })}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Typography variant="subtitle2" sx={{ minWidth: 120, textAlign: 'right' }}>
                      ${(item.qty * item.price).toFixed(2)}
                    </Typography>
                  </Stack>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
        {cartItems.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Stack spacing={2} sx={{ alignItems: 'flex-end', width: '100%', maxWidth: 640 }}>
              <Stack spacing={1} sx={{ alignItems: 'flex-end', width: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', justifyContent: 'flex-end' }}>
                  <TextField
                    label="Fee Description"
                    size="small"
                    value={feeDescription}
                    onChange={(e) => setFeeDescription(e.target.value)}
                    sx={{ width: 280 }}
                  />
                  <TextField
                    label="Fee Amount"
                    type="number"
                    size="small"
                    value={feeAmount}
                    onChange={(e) => {
                      const val = Math.max(0, Number(e.target.value || 0));
                      setFeeAmount(String(val));
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ width: 160 }}
                  />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', justifyContent: 'flex-end' }}>
                  <TextField
                    label="Discount Description"
                    size="small"
                    value={discountDescription}
                    onChange={(e) => setDiscountDescription(e.target.value)}
                    sx={{ width: 280 }}
                  />
                  <TextField
                    label="Discount Amount"
                    type="number"
                    size="small"
                    value={discountAmount}
                    onChange={(e) => {
                      const val = Math.max(0, Number(e.target.value || 0));
                      setDiscountAmount(String(val));
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ width: 160 }}
                  />
                </Stack>
              </Stack>
              {(() => {
                const subtotal = cartItems.reduce((sum, it) => sum + (it.qty * it.price), 0);
                const fee = Math.max(0, Number(feeAmount) || 0);
                const discount = Math.max(0, Number(discountAmount) || 0);
                const grand = Math.max(0, subtotal + fee - discount);
                return (
                  <Stack spacing={0.5} sx={{ alignItems: 'flex-end' }}>
                    <Typography variant="body2">Subtotal: ${subtotal.toFixed(2)}</Typography>
                    <Typography variant="body2">+ Fee: ${fee.toFixed(2)}</Typography>
                    <Typography variant="body2">- Discount: ${discount.toFixed(2)}</Typography>
                    <Divider flexItem sx={{ my: 0.5 }} />
                    <Typography variant="h6">Grand Total: ${grand.toFixed(2)}</Typography>
                  </Stack>
                );
              })()}
            </Stack>
          </Box>
        )}
      </Paper>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>Cannot create order. Please fix the following issues:</Typography>
          <Typography variant="body2">{submitError}</Typography>
          {Object.keys(fieldErrors).length > 0 && (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {Object.entries(fieldErrors).map(([field, error]) => (
                <li key={field}>
                  <Typography variant="body2">{error}</Typography>
                </li>
              ))}
            </Box>
          )}
        </Alert>
      )}

      <Box sx={{ display:'flex', gap:2, justifyContent:'flex-end' }}>
        <Button variant="outlined" onClick={() => navigate('/dashboard')} disabled={submitLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onConfirmSave} disabled={submitLoading}>
          {submitLoading ? 'Saving…' : 'Confirm & Save'}
        </Button>
      </Box>

      {/* Variation selection dialog */}
      <Dialog open={varOpen} onClose={() => setVarOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select Variation</DialogTitle>
        <DialogContent dividers>
          {varLoading ? (
            <Typography variant="body2">Loading variations...</Typography>
          ) : variations.length === 0 ? (
            <Typography variant="body2">No variations available.</Typography>
          ) : (
            <>
              <List>
                {variations.map(v => (
                  <ListItem key={v.id} sx={{ alignItems: 'center' }}
                    secondaryAction={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                          label="Qty"
                          type="number"
                          size="small"
                          value={varQtyById[v.id] ?? 1}
                          onChange={(e) => {
                            const val = Math.max(1, parseInt(e.target.value || '1', 10));
                            setVarQtyById(prev => ({ ...prev, [v.id]: val }));
                          }}
                          inputProps={{ min: 1, step: 1 }}
                          sx={{ width: 100 }}
                        />
                        <Button variant="contained" onClick={() => onSelectVariation(v, varQtyById[v.id] ?? 1)}>Add</Button>
                      </Stack>
                    }>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle2">
                            {(v.attributes && v.attributes.length)
                              ? v.attributes.map(a => `${a.name}: ${a.option}`).join(', ')
                              : (v.name || varBaseProduct?.name)}
                          </Typography>
                          {(v.sku) && (
                            <Typography variant="caption" color="text.secondary">SKU: {v.sku}</Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2">Price: ${Number(v.price || v.regular_price || 0).toFixed(2)}</Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVarOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Removed edit dialog; total is non-editable and qty uses +/- controls */}

      {/* Delete confirm dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Item</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">Are you sure you want to remove this item from the cart?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => {
            if (deleteItem) removeItem(deleteItem.key);
            setDeleteOpen(false);
          }}>Remove</Button>
        </DialogActions>
      </Dialog>

      <AddDevoteeDialog
        open={addDevoteeOpen}
        onClose={() => setAddDevoteeOpen(false)}
        onPostPayment={onAddDevoteePostPayment}
      />
    </PageLayout>
  );
};

export default PostPaymentPage;
